const PLAY_RETRY_DELAYS = [100, 300, 750, 1500];
const PAUSE_RETRY_DELAYS = [...PLAY_RETRY_DELAYS, 3000, 5000, 10000];

const iframeStates = new WeakMap();

function clearRetryTimers(state) {
    state.retryTimers.forEach((timer) => clearTimeout(timer));
    state.retryTimers = [];
}

function getTrackedPlayer(iframe) {
    if (typeof window === 'undefined') return null;

    try {
        const player = window.YT?.get?.(iframe.id);

        // AYoutube IDs are based on the video ID, so the same video can legitimately
        // appear more than once on a page. Never let the tracker route a command to a
        // different iframe which happens to share this ID.
        if (typeof player?.getIframe !== 'function') return null;
        return player.getIframe() === iframe ? player : null;
    } catch {
        return null;
    }
}

function parsePlayerMessage(data) {
    if (typeof data === 'object') return data;
    if (typeof data !== 'string') return null;

    try {
        return JSON.parse(data);
    } catch {
        return null;
    }
}

function isDesiredPlayerState(desiredState, playerState) {
    if (desiredState === 'playing') return playerState === 1;
    return playerState === 0 || playerState === 2;
}

function postCommand(iframe, command) {
    iframe.contentWindow?.postMessage?.(
        JSON.stringify({ event: 'command', func: command, args: '' }),
        '*',
    );
}

function applyDesiredState(iframe, state) {
    const command = state.desiredState === 'playing' ? 'playVideo' : 'pauseVideo';
    const player = getTrackedPlayer(iframe);

    if (typeof player?.[command] === 'function') {
        try {
            player[command]();
            return;
        } catch {
            // The tracker can expose a player before the iframe is ready. Fall back to postMessage and retry.
        }
    }

    postCommand(iframe, command);
}

function scheduleDesiredState(iframe, state) {
    clearRetryTimers(state);
    state.version += 1;
    const version = state.version;

    applyDesiredState(iframe, state);

    const retryDelays = state.desiredState === 'playing'
        ? PLAY_RETRY_DELAYS
        : PAUSE_RETRY_DELAYS;

    state.retryTimers = retryDelays.map((delay) => setTimeout(() => {
        if (state.version !== version) return;
        applyDesiredState(iframe, state);
    }, delay));
}

function getIframeState(iframe) {
    const existingState = iframeStates.get(iframe);
    if (existingState) return existingState;

    const state = {
        desiredState: 'paused',
        retryTimers: [],
        version: 0,
        onLoad: null,
        onMessage: null,
        source: null,
    };

    state.onLoad = () => scheduleDesiredState(iframe, state);
    state.onMessage = (event) => {
        if (event.source !== iframe.contentWindow) return;

        const message = parsePlayerMessage(event.data);
        if (message?.event === 'onReady') {
            scheduleDesiredState(iframe, state);
            return;
        }

        const playerState = message?.event === 'infoDelivery'
            ? message.info?.playerState
            : undefined;

        if (typeof playerState !== 'number') return;
        if (state.desiredState === 'playing') {
            // Once the player responds, leave its visible controls in charge. In particular,
            // do not undo a pause explicitly requested by the visitor.
            clearRetryTimers(state);
            return;
        }

        if (isDesiredPlayerState(state.desiredState, playerState)) {
            clearRetryTimers(state);
            return;
        }

        applyDesiredState(iframe, state);
    };

    iframe.addEventListener?.('load', state.onLoad);
    window.addEventListener?.('message', state.onMessage);
    iframeStates.set(iframe, state);

    return state;
}

function sourcesMatch(currentSource, desiredSource) {
    try {
        const base = window.location?.href || 'https://localhost/';
        const current = new URL(currentSource, base);
        const desired = new URL(desiredSource, base);

        if (current.origin !== desired.origin || current.pathname !== desired.pathname) return false;
        if (desired.hash && current.hash !== desired.hash) return false;

        return Array.from(desired.searchParams.entries()).every(([name, value]) => (
            current.searchParams.getAll(name).includes(value)
        ));
    } catch {
        return currentSource === desiredSource;
    }
}

function ensureIframeSource(iframe, state) {
    const currentSource = iframe.getAttribute?.('src');
    const desiredSource = iframe.dataset?.src;
    if (!desiredSource) return;

    const sourceChanged = state.source && state.source !== desiredSource;
    const currentSourceDiffers = currentSource &&
        !sourcesMatch(currentSource, desiredSource);

    if (!currentSource || sourceChanged || currentSourceDiffers) {
        iframe.setAttribute?.('src', desiredSource);
    }

    state.source = desiredSource;
}

export function playYoutubeIframe(iframe) {
    if (!iframe) return false;

    const state = getIframeState(iframe);
    state.desiredState = 'playing';
    ensureIframeSource(iframe, state);
    scheduleDesiredState(iframe, state);

    return true;
}

export function pauseYoutubeIframe(iframe) {
    if (!iframe) return false;

    const state = getIframeState(iframe);
    state.desiredState = 'paused';
    scheduleDesiredState(iframe, state);

    return true;
}

export function releaseYoutubeIframe(iframe) {
    const state = iframeStates.get(iframe);
    if (!state) return;

    clearRetryTimers(state);
    iframe.removeEventListener?.('load', state.onLoad);
    window.removeEventListener?.('message', state.onMessage);
    iframeStates.delete(iframe);
}
